import { useState, useEffect } from 'react';
import { CURRENCY_SYMBOLS, FALLBACK_CURRENCY } from '../utils/pricing';
import { attendiValuta } from '../utils/valuta';

const BASE = process.env.REACT_APP_API_URL || '/api';

// UNA RICHIESTA SOLA PER VALUTA (2026-09-12). Undici componenti chiamano
// questo hook e montano insieme: senza questo, undici richieste identiche
// partivano nello stesso istante. Si condivide solo la richiesta IN VOLO, non
// il risultato: appena si risolve la voce sparisce, cosi' nessuno legge un
// prezzo vecchio piu' tardi nella sessione.
const inVolo = new Map();

function chiediPrezzi(qs, token) {
  const chiave = `${qs}|${token ? 'con-token' : 'senza-token'}`;
  const gia = inVolo.get(chiave);
  if (gia) return gia;
  const p = fetch(`${BASE}/geo/pricing${qs}`, token ? { headers: { Authorization: `Bearer ${token}` } } : undefined)
    .then((r) => r.json());
  inVolo.set(chiave, p);
  p.catch(() => {}).then(() => { if (inVolo.get(chiave) === p) inVolo.delete(chiave); });
  return p;
}

// Single source of truth for prices/discounts shown on the site — fetched
// fresh from GET /api/geo/pricing (backend/config/founderOffer.js) instead of
// a hardcoded table, so the founder-pricing deadline flips automatically at
// midnight with no frontend deploy, and the number shown here can never say
// something the backend wouldn't actually charge (see the 2026-08-26 fix:
// the old frontend/src/utils/pricing.js named the wrong deadline entirely,
// 2026-11-11 instead of 2026-08-31).
//
// founderPricingActive defaults to true while the fetch is in flight (a
// single same-origin request, resolves in well under a second in practice)
// rather than flashing the post-deadline price/copy for a moment on every
// page load — a brief false-true window right at the exact deadline instant
// is a acceptable tradeoff against a visible flash on every load before it.
// Minimal shape check before trusting a /pricing response — added
// 2026-08-27 after one reached a real client empty/malformed (200 OK, but
// no usable body: plausibly a cache, proxy, or service worker somewhere in
// the path, including whatever Facebook/Instagram's in-app browser does to
// requests) and silently hid the 2TB launch banner from nearly all paid
// traffic, with nothing recording that it had even happened. `data` is only
// ever set from a response that passes this — see the comment on
// founderPricingActive below for what happens otherwise.
function isValidPricingResponse(d) {
  return !!(d && typeof d.founderPricingActive === 'boolean' && d.prices && d.promo2tb);
}

// Server-side record of "the client got something it couldn't use" — see
// routes/geo.js's matching endpoint. Best-effort: if this itself fails,
// there's nothing more useful to do than swallow it, same as any other
// telemetry call.
function reportPricingValidationFailure(d) {
  fetch(`${BASE}/geo/pricing-validation-failed`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ shape: JSON.stringify(d).slice(0, 200) }),
  }).catch(() => {});
}

// `currency` (optional) — pass the caller's own hooks/useCurrency() value
// when the caller reads userPromo2tb.promo's numbers (VaultPage, PlansPage,
// PromoBanner2TB, HomePage all do). Threaded straight through to the
// backend as ?currency=, which prefers it over the stale user.currency
// stamped once at registration (see routes/geo.js) — found live 2026-08-30:
// without this, the vault button displayed a price in the user's
// REGISTRATION-time currency while checkout charged in their CURRENT
// hooks/useCurrency() currency (live-detected, or manually overridden) —
// two different sources for the same number, which is exactly the
// "eventually diverge" bug this parameter exists to close. Omit it only for
// a caller that never reads userPromo2tb.promo's currency-specific numbers.
export function usePricing(currency) {
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    // NIENTE PREZZI PRIMA DI SAPERE LA VALUTA (2026-09-12). Il valore che
    // arriva qui parte da FALLBACK_CURRENCY (USD) e diventa quello vero solo
    // quando la rilevazione risponde: chiedere subito significava un giro di
    // prezzi in dollari mostrato per un istante anche a un italiano. Si
    // aspetta la valuta decisa e, se quella del chiamante non e' ancora
    // quella, non si chiede niente — l'effetto ripartira' da solo con il
    // valore giusto. Chi chiama senza valuta (currency undefined) non e'
    // toccato: per lui non c'e' niente da aspettare.
    // Soft-auth (2026-08-30): attaches the session token when one exists so
    // the backend can also return this specific user's REAL 2TB welcome-
    // window eligibility (userPromo2tb, see routes/geo.js) — the request
    // works identically for a logged-out visitor either way, and this hook
    // has no other way to reach the auth context (useAuth isn't a hook this
    // file can call from inside its own effect setup without becoming a
    // provider-order dependency for every page that renders a promo banner).
    let token = null;
    try { token = localStorage.getItem('zv_token'); } catch {}
    const qs = currency ? `?currency=${encodeURIComponent(currency)}` : '';
    (async () => {
      if (currency) {
        const decisa = await attendiValuta();
        if (cancelled || currency !== decisa) return;
      }
      chiediPrezzi(qs, token)
      .then(d => {
        if (cancelled) return;
        if (isValidPricingResponse(d)) { setData(d); return; }
        // Malformed/empty — deliberately do NOT setData(d). data stays null,
        // which is what keeps founderPricingActive/priceOf/tb2LaunchPromo on
        // their fail-open defaults below, exactly as if the fetch had never
        // resolved at all. Reported so this is measured, not just tolerated.
        reportPricingValidationFailure(d);
      })
      .catch(() => {});
    })();
    return () => { cancelled = true; };
  }, [currency]);

  const loading = !data;
  // FAIL OPEN, NEVER FAIL CLOSED. `data` is null in three cases: the fetch
  // hasn't resolved yet, it rejected/threw, or it resolved but failed
  // isValidPricingResponse above — all three land here and all three must
  // default to true. Every consumer of this hook (PromoBar, PromoBanner2TB,
  // the "founder offer" badges on Home/Plans) inherits this default, so
  // getting it backwards hides the promo instead of showing it — which is
  // exactly what happened 2026-08-27: a malformed-but-200-OK response hid
  // the entire 2TB launch banner from Facebook/Instagram's in-app browser
  // traffic, nearly all of this site's paid traffic, for an unknown length
  // of time before anyone noticed. If you're touching this line: the safe
  // direction for a promo is ALWAYS show, never hide.
  const founderPricingActive = data ? data.founderPricingActive : true;
  const deadline = data ? new Date(data.deadline) : null;
  const daysLeft = deadline ? Math.max(0, Math.ceil((deadline.getTime() - Date.now()) / 86400000)) : 0;

  function normalizeCurrency(currency) {
    return CURRENCY_SYMBOLS[currency] ? currency : FALLBACK_CURRENCY;
  }

  // { amount, symbol } for a plan in a given currency — same shape the old
  // static priceOf() returned, so call sites didn't need to change shape.
  function priceOf(planId, currency) {
    const cur = normalizeCurrency(currency);
    const amount = data?.prices?.[cur]?.[planId];
    if (amount == null) return null;
    return { amount, symbol: CURRENCY_SYMBOLS[cur] };
  }

  // 2TB's discount badge — { symbol, effective, struck, label } — or null
  // once founder pricing has ended (no discount to show). Values come
  // straight from the backend, which also computes what Stripe actually
  // charges — never re-derived client-side (that re-derivation is exactly
  // what caused the double-discount bug on the email side, 2026-08-26).
  function tb2LaunchPromo(currency) {
    const cur = normalizeCurrency(currency);
    const promo = data?.promo2tb?.[cur];
    if (!promo) return null;
    return { ...promo, symbol: CURRENCY_SYMBOLS[cur] };
  }

  // THIS logged-in user's real 2TB welcome-discount state (2026-08-30) —
  // eligible/expiresAt/promo, all computed server-side from
  // users.promo2tbExpiresAt (see routes/geo.js's soft-auth branch). null
  // while loading or for an anonymous visitor — callers must NOT fall back
  // to tb2LaunchPromo's always-on anonymous number for a logged-in user:
  // that would show €79 (or a countdown) to someone whose window already
  // closed. FAILS CLOSED on purpose, unlike founderPricingActive/
  // tb2LaunchPromo above — a per-user countdown with nothing behind it is
  // exactly the "fake deadline" spec point 6 says never to show, so the
  // safe default here is "no promo" until the server actually confirms one.
  //
  // .promo is enriched with `symbol` here, from the SAME `currency` the
  // backend resolved it against (routes/geo.js) — exactly mirroring
  // tb2LaunchPromo above. Found live 2026-08-30: this enrichment was
  // missing, so every consumer reading userPromo2tb.promo.symbol got
  // `undefined` and rendered "undefined79" on the vault upgrade button —
  // fixed once, here, at the single source every consumer reads from,
  // instead of a per-component fallback to a hardcoded "€".
  const userPromo2tb = data?.userPromo2tb
    ? { ...data.userPromo2tb, promo: data.userPromo2tb.promo ? { ...data.userPromo2tb.promo, symbol: CURRENCY_SYMBOLS[normalizeCurrency(data.userPromo2tb.currency)] } : null }
    : null;

  // Istante di fine dell'offerta 2TB, cosi' com'e' arrivato dal server. La
  // barra ci costruisce il conto alla rovescia: la data non e' scritta nel
  // frontend, quindi non puo' divergere da quella che decide il prezzo.
  // null finche' la risposta non e' arrivata: la barra resta nascosta invece
  // di mostrare un conto alla rovescia inventato.
  // ATTENZIONE alla forma della risposta: prices e' annidato PER VALUTA
  // ({ EUR: {...}, USD: {...} }), non piatto. Leggendolo al livello sbagliato
  // si ottiene undefined e la barra resta senza conto alla rovescia — ci sono
  // cascato, e il sintomo era conto="null" con la barra comunque visibile.
  const bloccoValuta = data?.prices?.[(currency || 'EUR').toUpperCase()]
    || data?.prices?.EUR || null;
  // La data di fine e' la stessa per tutte le valute, quindi va bene anche il
  // ripiego sul blocco EUR se la valuta richiesta non c'e'.
  const offerta2tbFine = bloccoValuta?.offerta2tbFine || null;
  const prezzo2tbDopo = bloccoValuta?.lifetime_2tb_dopo ?? null;
  // Fine dell'offerta 1TB a 49: il server la manda solo FINCHE' e' attiva
  // (poi null), quindi basta "c'e' o non c'e'" — il client non confronta
  // orologi. Le schede ci gatano la riga "fino al 13 settembre".
  const offerta1tbAttiva = !!bloccoValuta?.offerta1tbFine;
  // La data vera, non solo il si/no: le schede ci scrivono "fino al ..." e
  // devono leggerla da qui, non da una traduzione scritta a mano.
  const offerta1tbFine = bloccoValuta?.offerta1tbFine || null;
  // I limiti per piano (file massimo, numero di file): dal server, mai scritti
  // nel pacchetto. Null finche' la risposta non e' arrivata.
  const limiti = data?.limiti || null;

  // Posti del 5 TB: arriva dal server, che li deriva dai pagamenti del mese
  // e li tiene chiusi finche' il disco non c'e'. Il client non decide niente
  // e non conosce nessuna soglia: legge "aperti" e basta.
  const posti5tb = data?.posti5tb || null;
  // POSTI DI TUTTI E TRE I PIANI (2026-09-07). Oggetto per piano, cosi' le
  // card leggono posti[pianoId] senza sapere quali piani siano a numero
  // chiuso: un piano senza voce semplicemente non ne ha.
  const posti = data?.posti || null;

  return { loading, founderPricingActive, deadline, daysLeft, priceOf, tb2LaunchPromo, userPromo2tb, offerta2tbFine, prezzo2tbDopo, offerta1tbAttiva, offerta1tbFine, limiti, posti5tb, posti, currency };
}
